import { useMemo } from 'react';
import { useApprovalStore } from '@/stores/approvalStore.js';
import { useAskUserStore } from '@/stores/askUserStore.js';
import { ApprovalItem } from '@/components/sidebar/ApprovalItem.js';
import { InputRequestItem } from '@/components/sidebar/InputRequestItem.js';
import { AlertCircle } from 'lucide-react';

interface RequestsListProps {
  searchQuery: string;
  onOpenSession?: (sessionId: string | undefined) => void;
}

export function RequestsList({ searchQuery, onOpenSession }: RequestsListProps) {
  const approvalsMap = useApprovalStore((state) => state.approvals);
  const inputRequestsMap = useAskUserStore((state) => state.requests);

  // Convert approvals to array and sort
  const approvals = useMemo(() => {
    return Array.from(approvalsMap.values()).sort((a, b) => {
      // Sort by status (pending first) and then by timestamp (newest first)
      if (a.status === 'pending' && b.status !== 'pending') return -1;
      if (a.status !== 'pending' && b.status === 'pending') return 1;
      return b.timestamp - a.timestamp;
    });
  }, [approvalsMap]);

  // Convert input requests to array and sort
  const inputRequests = useMemo(() => {
    return Array.from(inputRequestsMap.values()).sort((a, b) => b.createdAt - a.createdAt);
  }, [inputRequestsMap]);

  // Filter approvals based on search query
  const filteredApprovals = approvals.filter((approval) => {
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();
    const toolNameMatch = approval.toolName.toLowerCase().includes(query);
    const messageMatch = approval.message?.toLowerCase().includes(query);
    const argsMatch = JSON.stringify(approval.toolArgs).toLowerCase().includes(query);

    return toolNameMatch || messageMatch || argsMatch;
  });

  // Filter input requests based on search query
  const filteredInputRequests = inputRequests.filter((req) => {
    if (!searchQuery) return true;

    const q = searchQuery.toLowerCase();
    return (
      req.prompt.toLowerCase().includes(q) ||
      (req.title && req.title.toLowerCase().includes(q)) ||
      (req.fromAgent && req.fromAgent.toLowerCase().includes(q))
    );
  });

  // Separate pending and completed approvals
  const pendingApprovals = filteredApprovals.filter((a) => a.status === 'pending');
  const completedApprovals = filteredApprovals.filter((a) => a.status !== 'pending');

  const hasAnyItems = filteredApprovals.length > 0 || filteredInputRequests.length > 0;

  if (!hasAnyItems) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <AlertCircle className="h-12 w-12 text-muted-foreground/50 mb-3" />
        <p className="text-sm text-muted-foreground">
          {searchQuery ? 'No requests match your search' : 'No pending requests'}
        </p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4 space-y-3">
        {/* Input Requests - show first */}
        {filteredInputRequests.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Input Requests ({filteredInputRequests.length})
            </h3>
            {filteredInputRequests.map((req) => (
              <InputRequestItem key={req.requestId} request={req} onOpenSession={onOpenSession} />
            ))}
          </div>
        )}

        {/* Pending approvals */}
        {pendingApprovals.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Pending Approvals ({pendingApprovals.length})
            </h3>
            {pendingApprovals.map((approval) => (
              <ApprovalItem key={approval.approvalId} approval={approval} />
            ))}
          </div>
        )}

        {/* Completed approvals */}
        {completedApprovals.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Completed ({completedApprovals.length})
            </h3>
            {completedApprovals.map((approval) => (
              <ApprovalItem key={approval.approvalId} approval={approval} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
